import { Button } from "@msuite/ui/button";
import { useTranslation } from "@msuite/ui/localization";
import { Popover } from "@msuite/ui/popover";
import { IconTag } from "@msuite/ui/tag";
import { Tooltip } from "@msuite/ui/tooltip";
import { moment } from "@msuite/utils/date";
import type { Tables } from "@msuite/utils/types";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { TicketCardDateCalendar } from "./ticket-card-date-calendar";

/** Props Interface */
interface TicketCardDateProps {
	ticket: Tables<"tickets">;
}

export function TicketCardDate({ ticket }: TicketCardDateProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { updateIsDenied } = useTicketRules();

	/** Guard */
	if (!ticket.startDate && !ticket.endDate) return null;

	const isWeekMode = ticket.dateMode === "week";

	const startDate = ticket.startDate
		? isWeekMode
			? `KW ${moment(ticket.startDate).format("W")}`
			: moment(ticket.startDate).dateFormat()
		: null;
	const endDate = ticket.endDate
		? isWeekMode
			? `KW ${moment(ticket.endDate).format("W")}`
			: moment(ticket.endDate).dateFormat()
		: null;

	const dates = (
		<div className="h-stack gap-4">
			{startDate && (
				<Tooltip label={t("dates.start_date")}>
					<IconTag
						color="emerald"
						iconName="ArrowRightFromLine"
					>
						{startDate}
					</IconTag>
				</Tooltip>
			)}
			{endDate && (
				<Tooltip label={t("dates.end_date")}>
					<IconTag
						color="red"
						iconName="ArrowRightToLine"
					>
						{endDate}
					</IconTag>
				</Tooltip>
			)}
		</div>
	);

	/** Render */
	// The calendar behind the tags is a write on the ticket, so a caller whose `update Ticket` rule
	// does not cover this one still reads when it is due — the tags just stop opening.
	if (updateIsDenied(ticket)) return <div className="px-1 **:text-xs!">{dates}</div>;

	return (
		<Popover
			trigger="click"
			render={() => (
				<TicketCardDateCalendar
					ticketId={ticket.id}
					projectId={ticket.projectId}
					startDate={ticket.startDate as string}
					endDate={ticket.endDate as string}
					dateMode={(ticket.dateMode as "date" | "week" | null) ?? null}
				/>
			)}
		>
			<Button
				variant="plain"
				size="xxs"
				className="**:text-xs!"
			>
				{dates}
			</Button>
		</Popover>
	);
}
